import i18next, { TOptions } from 'i18next';
import isNumber from 'lodash/isNumber';

import { ValueOf } from 'src/utils/types';
import { isAbsoluteUrl } from 'src/utils/url';
import { Enums } from 'src/models/enums';

type MenuItemResponse = {
  ID: number;
  CategoryID: number;
  Url: string;
  Title: string | null;
};

type OrganizerOrganizationResponse = {
  Url: string;
  Name: string;
  IsCurrentOrganization: boolean;
};

export type SidebarModelResponse = {
  MenuItems: MenuItemResponse[];
  OrganizerOrganizations: OrganizerOrganizationResponse[];
  PlusSubscriptionButtonRedirectUrl: string | null;
  IsVisible: true;
  OrganizationSubscriptionType: ValueOf<typeof Enums.organizations.OrganizationSubscriptionTypeCode>;
  DisplayPlusSubscriptionButton: boolean;
};

export type SidebarMenuItemType = {
  openNewTab: boolean;
  selected: boolean;
  readyAsSPA: boolean;
  id: number;
  title: string;
  url: string;
  items: SidebarMenuItemType[];
  testId: string;
};

export type SidebarOrganizerOrganizationType = {
  isCurrentOrganization: boolean;
  name: string;
  url: string;
};

export type ItemCategoryType = {
  items: SidebarMenuItemType[];
  id: number;
};

export type SidebarModelType = {
  organizations: SidebarOrganizerOrganizationType[];
  categories: ItemCategoryType[];
};

type AllowedItemsType = ({ id: number; items: AllowedItemsType } | number)[];

export enum MenuItemEnum {
  MyCampaigns = 1,
  NewCampaign,
  Branding,
  Profile,
  Funding, // Payment Processing
  Integrations,
  Users,
  SupporterCenter,
  DonationPages,
  HelpCenter,
  StartGuide,
  Settings,
  Administration,
  PartnerAdministration,
  SwitchAccount,
  Supporters,
  EmailMarketing,
  PlusSubscriptionBilling,
  OrganizationPricing,
  PledgeFunding,
  OrganizationReports,
}

export const prepareLeftMenuModel = (
  serverModel: SidebarModelResponse,
  orgId: string | null,
  partnerName: string
): SidebarModelType => {
  const translate = (str: string, options?: TOptions) => i18next.t(`leftMenu.${str}`, options);

  const getTitleItem = (id: number, defaultTitle: string | null): string => {
    switch (id) {
      case MenuItemEnum.MyCampaigns:
        return defaultTitle || translate('myExperiences');
      case MenuItemEnum.NewCampaign:
        return defaultTitle || translate('createExperience');
      case MenuItemEnum.Branding:
        return translate('branding');
      case MenuItemEnum.Profile:
        return translate('profile');
      case MenuItemEnum.Funding:
        return translate('funding');
      case MenuItemEnum.Integrations:
        return translate('integrations');
      case MenuItemEnum.Users:
        return translate('users');
      case MenuItemEnum.SupporterCenter:
        return translate('supportCenter');
      case MenuItemEnum.DonationPages:
        return translate('donationList');
      case MenuItemEnum.HelpCenter:
        return translate('helpCenter');
      case MenuItemEnum.StartGuide:
        return translate('startGuide');
      case MenuItemEnum.Administration:
      case MenuItemEnum.PartnerAdministration: // partner administration
        return translate('administration');
      case MenuItemEnum.SwitchAccount:
        return translate('switchAccount');
      case MenuItemEnum.Supporters:
        return translate('supporters');
      case MenuItemEnum.EmailMarketing:
        return translate('emailMarketing');
      case MenuItemEnum.PlusSubscriptionBilling:
        return translate('billing');
      case MenuItemEnum.OrganizationPricing:
        return translate('pricing');
      case MenuItemEnum.PledgeFunding:
        return translate('pledgeFunding');
      case MenuItemEnum.Settings:
        return translate('settings');
      case MenuItemEnum.OrganizationReports:
        return translate('reports');
      default:
        return '';
    }
  };

  const replaceUrlPlaceholderToOrgUrl = (url: string): string => url.replace(':id', orgId || '');

  const allowedMenuItems: AllowedItemsType[] = [
    [
      MenuItemEnum.StartGuide,
      MenuItemEnum.MyCampaigns,
      MenuItemEnum.Supporters,
      MenuItemEnum.EmailMarketing,
      MenuItemEnum.OrganizationReports,
    ],
    [
      MenuItemEnum.HelpCenter,
      {
        id: MenuItemEnum.Settings,
        items: [
          MenuItemEnum.Branding,
          MenuItemEnum.Profile,
          MenuItemEnum.Funding,
          MenuItemEnum.OrganizationPricing,
          MenuItemEnum.Integrations,
          MenuItemEnum.Users,
          MenuItemEnum.DonationPages,
          MenuItemEnum.PlusSubscriptionBilling,
          MenuItemEnum.SupporterCenter,
        ],
      },
    ],
    [
      MenuItemEnum.Administration,
      MenuItemEnum.PartnerAdministration,
      MenuItemEnum.PledgeFunding,
      MenuItemEnum.SwitchAccount,
    ],
  ];

  const categories: ItemCategoryType[] = [
    { id: 1, items: [] },
    { id: 2, items: [] },
    { id: 3, items: [] },
  ];

  /** is it an SPA page */
  // const isThereAnyReact = getDOMElement('#r-body-wrapper');

  const pushToCategories = (
    position: number,
    { ID, Url, Title }: { ID: number; Url: string; Title: string | null },
    itemIndex?: number
  ): number => {
    if (position < 0) {
      return -1;
    }

    const isSpaLinkSidebar = [
      MenuItemEnum.MyCampaigns,
      MenuItemEnum.DonationPages,
      MenuItemEnum.StartGuide,
      MenuItemEnum.Administration,
      MenuItemEnum.PartnerAdministration,
      MenuItemEnum.Users,
      MenuItemEnum.SupporterCenter,
      MenuItemEnum.Integrations,
      MenuItemEnum.Funding,
      MenuItemEnum.Branding,
      MenuItemEnum.Profile,
      MenuItemEnum.Supporters,
      MenuItemEnum.OrganizationPricing,
      MenuItemEnum.OrganizationReports,
    ].includes(ID); // inner React links

    const testIds: Record<number, string> = {
      [MenuItemEnum.MyCampaigns]: 'campaignsLeftMenu',
      [MenuItemEnum.NewCampaign]: 'newCampaignLeftMenu',
      [MenuItemEnum.Branding]: 'brandingLeftMenu',
      [MenuItemEnum.Profile]: 'profileLeftMenu',
      [MenuItemEnum.Funding]: 'paymentProcessingLeftMenu',
      [MenuItemEnum.Integrations]: 'integrationsLeftMenu',
      [MenuItemEnum.Users]: 'userManagementLeftMenu',
      [MenuItemEnum.SupporterCenter]: 'supporterCenterLeftMenu',
      [MenuItemEnum.DonationPages]: 'donationPagesLeftMenu',
      [MenuItemEnum.HelpCenter]: 'helpCenterLeftMenu',
      [MenuItemEnum.StartGuide]: 'startGuideLeftMenu',
      [MenuItemEnum.Settings]: 'settingsLeftMenu',
      [MenuItemEnum.Administration]: 'administrationLeftMenu',
      [MenuItemEnum.PartnerAdministration]: 'partnerAdministrationLeftMenu',
      [MenuItemEnum.SwitchAccount]: 'switchOrganizationLeftMenu',
      [MenuItemEnum.Supporters]: 'donorProfilesLeftMenu',
      [MenuItemEnum.EmailMarketing]: 'emailMarketingLeftMenu',
      [MenuItemEnum.OrganizationReports]: 'reportsLeftMenu',
      [MenuItemEnum.PlusSubscriptionBilling]: 'billingLeftMenu',
      [MenuItemEnum.OrganizationPricing]: 'pricingLeftMenu',
      [MenuItemEnum.PledgeFunding]: 'pledgeFundingLeftMenu',
    };

    const getItem = (id: number, url: string, defaultTitle: string | null): SidebarMenuItemType => ({
      openNewTab: isAbsoluteUrl(url),
      selected: true,
      id,
      title: getTitleItem(id, defaultTitle),
      url: replaceUrlPlaceholderToOrgUrl(url),
      readyAsSPA: isSpaLinkSidebar,
      items: [],
      testId: testIds[id],
    });

    if (itemIndex && ~itemIndex) {
      return categories[position].items[itemIndex].items.push(getItem(ID, Url, Title));
    }

    return categories[position].items.push(getItem(ID, Url, Title));
  };

  const generateItems = (items: AllowedItemsType, index: number, subItemsIndex?: number) => {
    items.forEach((item) => {
      if (!isNumber(item)) {
        const subItemsExist = serverModel.MenuItems.some((menuItem) => item.items.includes(menuItem.ID));

        if (subItemsExist) {
          const subItemsItemIndex = pushToCategories(index, { ID: item.id, Url: '', Title: null }) - 1;

          generateItems(item.items, index, subItemsItemIndex);
        }

        return;
      }

      const foundItem = serverModel.MenuItems.find((menuItem) => item === menuItem.ID);

      if (foundItem) {
        pushToCategories(index, foundItem, subItemsIndex);
      }
    });
  };

  allowedMenuItems.forEach((items, index) => {
    generateItems(items, index);
  });

  /** additional button in the bottom */
  if (serverModel.DisplayPlusSubscriptionButton) {
    categories
      .find(({ id }) => id === 3)
      ?.items.push({
        openNewTab: false,
        selected: true,
        id: Enums.user.sidebarItemTypeCode.SubscriptionButton,
        title: translate('subscribeToPremium', { partner: partnerName }),
        url: serverModel.PlusSubscriptionButtonRedirectUrl || '',
        readyAsSPA: true,
        items: [],
        testId: serverModel.PlusSubscriptionButtonRedirectUrl ? 'subscriptionLinkLeftMenu' : 'subscriptionLeftMenu',
      });
  }

  return {
    categories: categories.filter(({ items }) => items.length > 0),
    organizations: serverModel.OrganizerOrganizations.map(({ Name, Url }) => ({
      isCurrentOrganization: Url === orgId,
      name: Name,
      url: Url,
    })),
  };
};
